<template>
  <v-card>
    <v-icon class="close" @click="close">$sl-close</v-icon>
    <v-card-title>
      <span class="display-1">LÄGG TILL LÄRLING</span>
    </v-card-title>

    <v-card-text class="mt-n4">
      <v-form ref="form">
        <PersonFind ref="personfind" v-model="person" />

        <MessageBox type="warning" v-if="isNotApprentice">
          <p>Denna person är inte en lärling</p>  
        </MessageBox> 

        <div v-if="personSelected">
          <p class="mb-6">
            När du lägger till en lärling till ett företag kopierar du en av lärlingens befintliga anställningar som
            lärling, här kallad lärlingsplats. Lärlingen kopplas då till det valda företaget och kopplas från det tidigare företaget.
          </p>
          <h6 class="bold text-uppercase mb-5">KOPIERA BEFINTLIG INFORMATION OM LÄRLINGEN TILL {{ selectedCompany.name }}</h6>
           
          <v-select
            v-model="sourceApprentincenship"
            :items="existingApprenticeships"
            outlined
            item-text="label"
            item-value="apprenticeship"
            label="Lärlingsplats *"
            :placeholder="sourceApprentincenship ? '' : 'Välj den information som ska kopieras'"
            @change="sourceApprentincenshipChanged"
            return-object
            :rules="[rules.required]"
            :disabled="!apprentice.apprenticeships.length"
          ></v-select>

          <!-- Show these fields IF company has departments -->
          <div v-if="hasDepartments">
            <h6 class="bold text-uppercase mb-5">LÄRLINGENS AVDELNING HOS {{ selectedCompany.name }}</h6>
            <v-select
              v-model="department"
              :items="allDepartments"
              outlined
              item-text="name"
              item-value="id"
              label="Avdelning *"
              :placeholder="department ? '' : 'Välj avdelning...'"
              @change="selectDepartment"
              :rules="[rules.required]"
              return-object
            ></v-select>

            <h6 class="bold text-uppercase mb-5">LÄRLINGENS ARBETSPLATSER HOS {{ selectedCompany.name }}</h6>
            <v-select
              v-model="apprentincenship.workplace"
              :items="allWorkplaces"
              outlined
              item-text="name"
              item-value="id"
              label="Arbetsplats *"
              :placeholder="apprentincenship.workplace ? '' : 'Välj arbetsplats…'"
              :rules="[rules.required]"
              return-object
            ></v-select>

            <h6 class="bold text-uppercase mb-5">LÄRLINGENS HANDLEDARE HOS {{ selectedCompany.name }}</h6>
            <v-select
              v-model="apprentincenship.instructor"
              :items="departmentInstructors"
              outlined
              :item-text="item => instructorNameWithNumberOfApprentices(item, apprenticeshipsList)"
              item-value="id"
              label="Handledare *"
              :placeholder="apprentincenship.instructor ? '' : 'Välj handledare…'"
              :rules="[rules.required]"
              return-object
              no-data-text="Inga handledare uppfyller kraven."
            ></v-select>
          </div>

          <div v-if="!hasDepartments">
            <h6 class="bold text-uppercase mb-5">LÄRLINGENS HANDLEDARE HOS {{ selectedCompany.name }}</h6>
            <v-select
              v-model="apprentincenship.instructorId"
              :items="filteredInstructors"
              outlined
              :item-text="item => instructorNameWithNumberOfApprentices(item, apprenticeshipsList)"
              item-value="id"
              label="Handledare *"
              :placeholder="apprentincenship.instructorId ? '' : 'Välj handledare…'"
              :rules="[rules.required]"
              no-data-text="Inga handledare uppfyller kraven."
            ></v-select>
          </div>

          <h6 class="bold text-uppercase mb-5">LÄRLINGENS ANSTÄLLNINGSFORM HOS {{ selectedCompany.name }}</h6>
          <MessageBox class="mb-6">
            <p class="body-3">
              För mer information om reglerna och val av anställningsform, var vänlig se
              <a href="https://byns.sharepoint.com/SitePages/Hitta-anvisningar.aspx" target="_blank"
                >yrkesutbildningsavtalet och dess anvisningar</a
              >.
            </p>
          </MessageBox>
          <SelectTypes
            ref="employmentType"
            :placeholder="apprentincenship.employmentTypeId ? '' : 'Välj anställningsform…'"
            selectFn="listEmploymentTypes"
            label="Anställningsform"
            v-model="apprentincenship.employmentTypeId"
            :rules="[rules.required]"
          />

          <div v-if="!hasDepartments">
            <h6 class="bold text-uppercase mb-5">LÄRLINGENS ARBETSPLATSER HOS {{ selectedCompany.name }}</h6>
            <MultiSelect
              ref="multiSelect"
              :list="allWorkplaces"
              label="Arbetsplats"
              v-model="apprentincenship.workplacesIds"
              :placeholder="apprentincenship.workplacesIds ? '' : 'Välj arbetsplatser…'"
              :rules="[rules.required]"
            />
          </div>

          <div>
            <h6 class="bold text-uppercase mb-5">AVSLUTAD ANSTÄLLNING HOS FÖRETAG {{ get(sourceApprentincenship, 'apprenticeship.company.name', "") }} </h6>

            <p v-if="isDisconnectedApprentice">
              Här visas slutdatum från tidigare företag. 
              <br />
              <span class="grey-text font-italic">(Går ej att ändra, lärlingen har redan avslutat anställningen enligt datum nedan)</span>
            </p>

            <p v-else>
              {{ `Här kan du välja när lärlingsperioden upphör hos ${ currentApprenticeshipCompanyName || 'tidigare företag' }.` }}
            </p>
            
            <DatePicker
              v-model="previousApprentice.toDate"
              ref="datePicker"
              :icon="'$sl-calendar-date'"
              :label="'Slutdatum'"
              :width="'250'"
              outlined
              :min-date="get(sourceApprentincenship, 'apprenticeship.fromDate', null)"
              :disabled="isDisconnectedApprentice"
              :rules="[rules.required]"
            />

            <h6 class="bold text-uppercase mb-5">LÄRLINGSPLATSENS STARTDATUM HOS {{ selectedCompany.name }}</h6>
            <p>Välj datum för påbörjad anställning hos {{ selectedCompany.name }}.</p>

            <DatePicker
              v-model="newApprentice.fromDate"
              ref="datePicker"
              :icon="'$sl-calendar-date'"
              :label="'Startdatum'"
              :width="'250'"
              outlined
              :rules="[rules.required]"
            />
          </div>
        </div>
      </v-form>
    </v-card-text>

    <v-card-actions class="justify-end">
      <v-btn color="secondary" @click="close"> <v-icon>$sl-cancel</v-icon>Avbryt </v-btn>
      <v-btn v-if="!personSelected" :disabled="!person || !person.id || !canCreate" @click="selectPerson()">
        <v-icon>$sl-add</v-icon>Lägg till</v-btn
      >
      <v-btn v-if="personSelected" @click="createApprentice()" :disabled="!canCreate"> <v-icon>$sl-check</v-icon>Spara</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters} from 'vuex'
import {RulesMixin, InstructorAbilities, CanIDo} from '@/components/mixins'
import PersonFind from '@/components/common/PersonSelect/PersonFind'
import SelectTypes from '@/components/common/SelectTypes/SelectTypes.vue'
import DatePicker from '@/components/common/DatePicker'
import MultiSelect from '@/components/common/SelectTypes/MultiSelect'
import MessageBox from '@/components/common/MessageBox.vue'
import get from 'lodash/get'

// @group Company
// Styled CreateApprenticeship component

export default {
  name: 'CreateApprenticeship',

  props: {
    allDepartments: {
      type: Array,
      default: () => ([])
    },
    apprenticeshipsList: {
      type: Array,
      default: () => ([])
    },
  },

  mixins: [RulesMixin, InstructorAbilities, CanIDo],

  components: {
    PersonFind,
    SelectTypes,
    MultiSelect,
    DatePicker,
    MessageBox
  },
  data() {
    return {
      department: null,
      apprentincenship: {},
      apprentice: {},
      educations: [],
      person: null,
      sourceApprentincenship: null,
      instructors: [],
      workplaces: [],
      personSelected: false,
      existingApprenticeships: [],
      canContinue: true,
      currentApprenticeship: null,
      previousApprentice: { toDate: new Date().toISOString() },
      newApprentice: { fromDate: new Date().toISOString() }
    }
  },
  watch: {
    person: {
      handler: function () {
        if (this.personSelected && this.person) {
          this.selectPerson()
        }
        if (!this.person || !this.person.id) {
          this.personSelected = false
        }
        if(!this.person && !this.canContinue) {
          this.canContinue = true
        }
      },
      deep: true
    },

    'sourceApprentincenship.apprenticeship.manuallyEndedDate': {
      handler(value) {
        this.previousApprentice.toDate = (value ||new Date().toISOString())
      }
    }
  },
  created() {
    this.getEmployments()
  },
  mounted() {
    this.$nextTick(() => {
      const input = this.$refs.personfind.$el.querySelector('input')
      input.focus()
    })
  },
  computed: {
    ...mapGetters('company', ['selectedCompany']),
    ...mapGetters('basic', ['basicProfessions', 'hasProfessionOrientations', 'getProfessionById']),

    // get instructor name with number of apprentices respectively
    instructorNameWithNumberOfApprentices: () => (item, apprenticeships) => {
      const activeApprentices = item.instructor.apprenticeshipInstructors.filter((apprentice) => {
        const found = apprenticeships.find(appr => appr.id === apprentice.apprenticeship.id)
        if(new Date(found?.toDate) > new Date()) return apprentice
      })
      
      return `${item.fullName} (${activeApprentices.length} lärlingar)`
    },

    // get department instructors
    departmentInstructors() {
      const apprentice = this.existingApprenticeships?.find(apprentice => {
        return apprentice.apprenticeship.personId === this.person?.id
      })

      const departmentInstructorIds = this.department?.instructors?.map(depInst => depInst.id)
      const instructors = this.instructors?.filter(instructor => departmentInstructorIds?.includes(instructor.id))

      if(apprentice && this.currentApprenticeship) {
        const professionId = this.currentApprenticeship?.professionId
        const GU = apprentice.type === "QualificationTime"
        const machineQualificationsIds = this.apprentice.machinequalifications.map(mQ => mQ.machineId)

        const availableInstructors = instructors.map((item) => {
          return {...item, 
            ...{canHaveAnotherApprentice: this.canHaveAnotherApprentice(item.instructor, professionId, machineQualificationsIds, !GU),
            disabledInstructor: !Boolean(item.instructor.employed.email != '' && this.canHaveAnotherApprentice(item.instructor, professionId, machineQualificationsIds, !GU) &&
            this.haveCertificates(item.instructor, professionId, machineQualificationsIds, !GU))}
          }
        })

        return availableInstructors.filter(item => item.canHaveAnotherApprentice)
      }
    },

    // check if company has departments
    hasDepartments() {
      return !!this.selectedCompany.hasDepartments
    },

    currentApprenticeshipCompanyName() {
      return this.currentApprenticeship?.apprenticeship?.company?.name
    },

    allWorkplaces() {
      if (this.hasDepartments) {
        const instructors = get(this, 'department.instructors', [])
        const instructorIds = get(this, 'department.instructorIds', [])
        const instructor = instructors.find(inst => instructorIds.includes(inst.id))
        if (!instructor) return []

        const wpIds = get(this, 'department.workplaceIds', [])
        return this.selectedCompany.workplaces.filter(w =>
          w.departmentWorkplaces.find(dw => wpIds.includes(dw.workplaceId))
        )
      } else {
        return this.selectedCompany.workplaces
      }
    },

    filteredInstructors() {
        const apprentice = this.existingApprenticeships?.find(apprentice => {
          return apprentice.apprenticeship.personId === this.person?.id
        })

        if((apprentice && this.currentApprenticeship) || this.hasApprenticeship) {
          const professionId = this.currentApprenticeship?.professionId ?? this.apprentice.educations[0]?.professionId
          const isQualificationTime = apprentice?.type === "QualificationTime" ? true : false

          let mQIds = this.apprentice.machinequalifications?.map(mQ => mQ.machineId) || []

          if(!mQIds.length) {
            mQIds = this.apprentice.educations?.map(e => {
              if(e.educationProfessionOrientations.length > 0) {
                const machineIds = e.educationProfessionOrientations.map(o => o.professionMachineOrientation.machineId)
                return machineIds
              }
              return []
            }).flat()
          }

          return this.instructors.filter(i => {
            if(i.isInstructor && this.canHaveAnotherApprentice(i.instructor, professionId, mQIds, !isQualificationTime)) {
              return {
                id: i.id,
                employedId: i.employedId,
                fullName: i.fullName,
                instructor: i.instructor,
                instructorId: i.instructorId,
                instructorDepartmentIds: i.instructorDepartmentIds
              }
            }
          })
        }
    },

    hasApprenticeship() {
      return this.apprentice?.apprenticeships?.length > 0 || this.apprentice?.educations.some(edu => edu.qualificationTime || edu.qualificationTimeLength > 0)
    },

    isDisconnectedApprentice() {
      return !!get(this, 'sourceApprentincenship.apprenticeship.manuallyEndedDate', null)
    },

    isNotApprentice() {
      return !this.canContinue && !this.hasApprenticeship
    }
  },

  asyncComputed: {
    canCreate(){
      return this.canIDo('apprentice', 'create');
    },
  },

  methods: {
    get: get,
    selectDepartment(dep) {
      this.department = dep
    },

    selectPerson() {
      API.getPerson(this.person.id)
        .then((response) => {  
          this.apprentice = response.data
          if(this.hasApprenticeship) {
            API.listApprenticeships(this.person.id).then(response => {
              this.existingApprenticeships = response.data.reduce((acc, el) => {
              const profession = this.basicProfessions.find(profession => profession.id === el.professionId) || {}
              const isExpiredApprenticeship = new Date(el.toDate) <= new Date()

              if (el.companyId !== this.selectedCompany.id || el.manuallyEndedDate || isExpiredApprenticeship) {
                acc.push({
                  id: el.id,
                  label: el.company.name + ': ' + profession.name,
                  professionId: el.professionId,
                  apprenticeship: el,
                  type: el.type
                })
              }

              return acc
              }, [])

              this.personSelected = true
              return
            })
          }
          this.canContinue = false
      })
      .catch(() => {
          this.existingApprenticeships = []
        })
    },
    sourceApprentincenshipChanged(val) {
      this.currentApprenticeship = val
      this.apprentincenship.toDate = val.apprenticeship.toDate
    },
    getEmployments() {
      API.listCompanyEmployments(this.selectedCompany.id)
        .then(response => {
          response.data.forEach(el => {
            this.instructors.push({
              id: el.id,
              employedId: el.employed.id,
              instructor: el,
              isInstructor: el.isInstructor,
              fullName: this.$options.filters.fullName(el.employed),
              instructorId: el.instructorId,
              instructorDepartmentIds: el.instructorDepartmentIds
            })
          })
        })
        .catch(error => {
          console.log('get employemnts', error)
        })
    },
    close() {
      this.$refs.form.reset()
      this.apprentincenship = {}
      this.person = null
      this.sourceApprentincenship = null
      this.instructors = []
      this.workplaces = []
      this.personSelected = false
      this.$emit('close')
    },
    createApprentice() {
      const isValidated = this.$refs.form.validate()
      const isSelectTypesFormValidated = this.$refs.employmentType.$refs.selectTypesForm.validate()
      const isWithoutEmployment = Boolean(!this.apprentice.apprenticeships.length && this.apprentice.educations.some(e => e.qualificationTimeLength > 0))

      //- Validate date picker
      const hasPickedDates = this.$refs.datePicker.$refs.form.validate()

      if (!isValidated || !isSelectTypesFormValidated || !hasPickedDates) {
        return this.$events.fire('showError', 'Formuläret är inte ifyllt korrekt.')
      }

      const apprentice = {
        companyId: this.selectedCompany.id,
        personId: this.person.id,
        educationId: get(this, 'sourceApprentincenship.apprenticeship.educationId', null),
        instructorId: !this.hasDepartments ? this.apprentincenship.instructorId : this.apprentincenship.instructor.id,
        professionId: get(this, 'sourceApprentincenship.professionId', null),
        workplaceIds: !this.hasDepartments ? this.apprentincenship.workplacesIds : [this.apprentincenship.workplace.id],
        fromDate: new Date(this.newApprentice.fromDate),
        toDate: new Date(get(this, 'previousApprentice.manuallyEndedDate', get(this, 'previousApprentice.toDate', ''))),
        employmentTypeId: this.apprentincenship.employmentTypeId
      }

      let apiMethod = 'moveApprenticeship'

      if(isWithoutEmployment) {
        apiMethod = 'createApprenticeships'
      }

      API[apiMethod](isWithoutEmployment ? ({data: apprentice}) : get(this, 'sourceApprentincenship.apprenticeship.id', null),  this.selectedCompany?.id, {data: apprentice})
        .then(response => {
          this.$refs.form.reset()
          this.apprentincenship = {}
          this.person = null
          this.sourceApprentincenship = null
          this.instructors = []
          this.workplaces = []
          this.personSelected = false
          this.$events.fire('showInfo', 'Personen sparad.')
          this.$emit('back-to-list', true)
        })
        .catch(error => {
          const message = get(error, 'response.data.message', get(error, 'message', get(error, 'response.statusText','Okänt fel')))
          this.$events.fire('showError', `Ett fel uppstod vid flytt av lärlingen: ${message}`)
          console.log('save apprentice', error)
        })
    }
  }
}
</script>

<style lang="scss" scoped></style>
